import { ComponentRef, ElementRef, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, pairwise } from 'rxjs/operators';

interface Dialog {
  content: any;
  zIndex: number;
  ignoreEscKey: boolean;
  close: (() => void) | null;
  destroy: (() => void) | null;
  ignoreNavigationClosing: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class DialogsStoreService {
  private dialogs: Dialog[] = [];
  private zIndex = 10000;

  constructor(private router: Router) {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        pairwise(),
        filter(this.checkIsChangeRouteWithoutParams),
      )
      .subscribe(() => {
        this.removeAllByNavigation();
      });
  }

  add<T>(opt: {
    dialog: T | ComponentRef<T>;
    elementRef?: ElementRef;
    ignoreEscKey?: boolean;
    ignoreNavigationClosing?: boolean;
    destroy?: () => void;
    close?: () => void;
  }): { zIndex: number } {
    let item = this.dialogs.find((el) => this.equal(el, opt.dialog, opt.elementRef));

    if (item) {
      item.close = item.close || opt.close || null;
      item.destroy = item.destroy || opt.destroy || null;
    } else {
      item = {
        content: opt.dialog,
        zIndex: this.zIndex + 1,
        ignoreEscKey: !!opt.ignoreEscKey,
        close: opt.close || null,
        destroy: opt.destroy || null,
        ignoreNavigationClosing: !!opt.ignoreNavigationClosing,
      };

      this.dialogs.push(item);
    }

    return { zIndex: ++this.zIndex };
  }

  remove<T>(dialogInstance: T): void {
    const index = this.dialogs.findIndex((dialog) => this.equal(dialog, dialogInstance));
    this.removeByIndex(index);
  }

  closeByEscKey() {
    const lastDialogIndex = this.dialogs.length - 1;
    const dialog = this.dialogs[lastDialogIndex];

    if (dialog && !dialog.ignoreEscKey) {
      if (dialog.close) {
        dialog.close();
      } else {
        this.removeByIndex(lastDialogIndex);
      }
    }
  }

  removeAll() {
    this.removeByIndex(0);
  }

  private removeAllByNavigation(): void {
    const filteredDialogs = this.dialogs.filter((item) => {
      if (item.destroy && !item.ignoreNavigationClosing) {
        item.destroy();
      }
      return item.ignoreNavigationClosing;
    });
    this.zIndex -= this.dialogs.length - filteredDialogs.length;
    this.dialogs = filteredDialogs;
  }

  private removeByIndex(index: number) {
    if (index >= 0) {
      const itemsToDelete = this.dialogs.length - index;
      this.zIndex -= itemsToDelete;
      this.dialogs.splice(index, itemsToDelete).forEach((item) => item.destroy && item.destroy());
    }
  }

  private equal(dialog: Dialog, dialogInstance: any, elementRef?: ElementRef): boolean {
    if (dialog.content === dialogInstance) {
      return true;
    }
    if (!(dialog.content instanceof ComponentRef)) {
      return false;
    }
    if (dialog.content.instance === dialogInstance) {
      return true;
    }
    if (elementRef) {
      return (dialog.content.location.nativeElement as HTMLElement).contains(elementRef.nativeElement);
    }
    return false;
  }

  private checkIsChangeRouteWithoutParams = (events: [NavigationEnd, NavigationEnd]): boolean => {
    const oldUrl = this.getRoutePathWithoutParams(events, 0);
    const newUrl = this.getRoutePathWithoutParams(events, 1);

    return oldUrl !== newUrl;
  };

  private getRoutePathWithoutParams(eventsArray, eventIndex): string {
    return eventsArray[eventIndex].urlAfterRedirects.indexOf('?') === -1
      ? eventsArray[eventIndex].urlAfterRedirects
      : eventsArray[eventIndex].urlAfterRedirects.slice(0, eventsArray[eventIndex].urlAfterRedirects.indexOf('?'));
  }
}
